import { type DeviceInfo, isDesktopDevice } from '@/idv2/utils/get-device';
import type { TransferMode, TransferRequirements } from '../types';

type GetTransferModeParams = {
  device: DeviceInfo;
  isSocialMediaBrowser: boolean;
  isInIframe: boolean;
  isTransferFromDesktopToMobileDisabled?: boolean;
  missingRequirements: TransferRequirements;
  hasAppclipSupport: boolean;
};

/**
 * Determines which transfer mode to use based on device, browser, and requirements:
 * 1. Desktop (not mobile/tablet) + transfer enabled: qr
 * 2. Social media browser + doc requirement: sms
 * 3. Mobile, not in iframe: skip (already in a top-level context)
 * 4. Liveness requirement + webauthn support: new tab (passkey needs top-level context)
 * 5. Mobile with App Clip support: new tab
 * 6. Otherwise: skip
 */
const getTransferMode = ({
  device,
  isSocialMediaBrowser,
  isInIframe,
  isTransferFromDesktopToMobileDisabled,
  missingRequirements,
  hasAppclipSupport,
}: GetTransferModeParams): TransferMode => {
  const isDesktop = isDesktopDevice(device);

  // Desktop: qr code for user to scan with phone
  if (isDesktop && !isTransferFromDesktopToMobileDisabled) {
    return 'qr';
  }

  // Social media in-app browser: exit via sms link
  if (isSocialMediaBrowser) {
    // Only show sms if there are documents to collect, otherwise complete
    return missingRequirements.documents.length > 0 ? 'sms' : 'skip';
  }

  // On mobile in a top-level browsing context (not embedded), no transfer is needed
  if (!isDesktop && !isInIframe) {
    return 'skip';
  }

  // Remaining cases: mobile in an iframe, or desktop with transfer disabled
  // Both may need a new tab for passkey registration or App Clip
  if (missingRequirements.liveness && device.hasSupportForWebauthn) {
    return 'new-tab';
  }

  if (hasAppclipSupport) {
    return 'new-tab';
  }

  return 'skip';
};

export default getTransferMode;
